import { useCallback, useMemo } from 'react';
import { NetworkStatus } from '@apollo/client';

import { useCurrentGeoRegion } from '@ocx-app/lib/geolocation/use-current-geo-region.hook';

import { useGetExternalOffersQuery } from '@ocx/graphql';

import { createExternalOffersQueryVariables } from '../external-loyalty/external-offers.utils';

export type UseCategoryExternalBannersParams = {
  bannerIds?: string[] | null;
  skip?: boolean;
};

const isQueryInFlight = (networkStatus: NetworkStatus) =>
  networkStatus === NetworkStatus.loading ||
  networkStatus === NetworkStatus.setVariables ||
  networkStatus === NetworkStatus.refetch;

export const useCategoryExternalBanners = (params: UseCategoryExternalBannersParams) => {
  const { bannerIds, skip: skipParam = false } = params;
  const bannerConfigurationUuidIncludes = useMemo(() => bannerIds ?? [], [bannerIds]);
  const shouldFetch = !skipParam && bannerConfigurationUuidIncludes.length > 0;

  const {
    geoRegion,
    isResolving: isResolvingLocation,
    refresh: refreshGeoRegion,
  } = useCurrentGeoRegion({
    skip: !shouldFetch,
    refreshKey: bannerConfigurationUuidIncludes.join(','),
  });

  const variables = useMemo(
    () => createExternalOffersQueryVariables(bannerConfigurationUuidIncludes, geoRegion),
    [bannerConfigurationUuidIncludes, geoRegion],
  );
  const skip = !shouldFetch || isResolvingLocation;

  const { data, previousData, networkStatus, refetch } = useGetExternalOffersQuery({
    skip,
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const refetchWithLocation = useCallback(async () => {
    const region = await refreshGeoRegion();
    return refetch(createExternalOffersQueryVariables(bannerConfigurationUuidIncludes, region));
  }, [bannerConfigurationUuidIncludes, refreshGeoRegion, refetch]);

  return {
    // Keep last known banners while revalidating so UI does not flash empty.
    banners: data?.banners ?? previousData?.banners ?? [],
    loading: (!skip && isQueryInFlight(networkStatus)) || isResolvingLocation,
    refetch: skip ? undefined : refetchWithLocation,
  };
};
